import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface Organization {
  id: string;
  name: string;
  website?: string;
  credits: number;
  settings?: Record<string, any>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface OrganizationStats {
  id: string;
  name: string;
  credits: number;
  isActive: boolean;
  users: {
    total: number;
  };
  projects: {
    total: number;
    active: number;
    inactive: number;
  };
  createdAt: string;
}

interface OrganizationsState {
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentOrganizationStats: OrganizationStats | null;
  selectedOrganizationId: string | null;
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: OrganizationsState = {
  organizations: [],
  currentOrganization: null,
  currentOrganizationStats: null,
  selectedOrganizationId: null,
  status: 'idle',
  error: ''
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getMyOrganizations = createAsyncThunk('organizations/getMyOrganizations', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest('/organizations/me');
    return response.response || response;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getOrganization = createAsyncThunk('organizations/getOrganization', async (organizationId: string, { rejectWithValue }) => {
  try {
    const response = await apiRequest(`/organizations/${organizationId}`);
    return response.response || response;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const createOrganization = createAsyncThunk(
  'organizations/createOrganization',
  async (
    {
      name,
      website,
      userId
    }: {
      name: string;
      website?: string;
      userId: string;
    },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      // VALIDATION: Check if user already has an organization
      const state: any = getState();
      const existingOrgs = state.organizations.organizations;

      if (existingOrgs && existingOrgs.length > 0) {
        return rejectWithValue('You already belong to an organization. Each user can only belong to one organization.');
      }

      // 1. Create organization
      const orgResponse = await apiRequest('/organizations', {
        method: 'POST',
        body: JSON.stringify({
          name,
          website: website || undefined
        })
      });

      const organization = orgResponse.response || orgResponse;

      // Backend already adds the user as admin when creating the org,
      // creates free subscription, and grants signup credits - no need for add-user call

      // Refresh organizations list
      dispatch(getMyOrganizations());

      return organization;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateOrganization = createAsyncThunk(
  'organizations/updateOrganization',
  async ({ id, data }: { id: string; data: { name?: string; website?: string; settings?: Record<string, any> } }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/organizations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getOrganizationStats = createAsyncThunk(
  'organizations/getOrganizationStats',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/organizations/${organizationId}/stats`);
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const addUserToOrganization = createAsyncThunk(
  'organizations/addUserToOrganization',
  async (
    {
      organizationId,
      email,
      name,
      role = 'user'
    }: {
      organizationId: string;
      email: string;
      name: string;
      role?: 'user' | 'developer' | 'admin';
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest('/organizations/add-user', {
        method: 'POST',
        body: JSON.stringify({
          organizationId,
          email,
          name,
          role
        })
      });
      return response.response || response;
    } catch (error: any) {
      // Pass full error object for multi-org conflict detection
      // API returns: { error: { code: 'Conflict', message: '...' } }
      if (error?.error?.code === 'Conflict') {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  }
);

// switchOrganization removed - users can only belong to one organization

// ==============================|| SLICE ||============================== //

export const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setSelectedOrganization(state, action) {
      state.selectedOrganizationId = action.payload;
      const org = state.organizations.find((o) => o.id === action.payload);
      if (org) {
        state.currentOrganization = org;
      }
    },
    clearOrganizationsState(state) {
      state.organizations = [];
      state.currentOrganization = null;
      state.currentOrganizationStats = null;
      state.selectedOrganizationId = null;
      state.status = 'idle';
      state.error = '';
    }
  },
  extraReducers: (builder) => {
    // Get My Organizations
    builder
      .addCase(getMyOrganizations.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getMyOrganizations.fulfilled, (state, action) => {
        if (action.payload) {
          state.organizations = action.payload;
        }
        state.status = 'success';
        state.error = '';
      })
      .addCase(getMyOrganizations.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to fetch organizations';
      });

    // Get Organization
    builder
      .addCase(getOrganization.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getOrganization.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentOrganization = action.payload;
          state.selectedOrganizationId = action.payload.id;
          // Add to organizations array if not already there
          const exists = state.organizations.find((o) => o.id === action.payload.id);
          if (!exists) {
            state.organizations.push(action.payload);
          } else {
            // Update existing
            state.organizations = state.organizations.map((o) => (o.id === action.payload.id ? action.payload : o));
          }
        }
        state.status = 'success';
        state.error = '';
      })
      .addCase(getOrganization.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to fetch organization';
      });

    // Create Organization
    builder
      .addCase(createOrganization.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        if (action.payload) {
          state.organizations.push(action.payload);
          state.currentOrganization = action.payload;
          state.selectedOrganizationId = action.payload.id;
        }
        state.status = 'success';
        state.error = '';
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to create organization';
      });

    // Update Organization
    builder
      .addCase(updateOrganization.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        if (action.payload) {
          state.organizations = state.organizations.map((o) => (o.id === action.payload.id ? action.payload : o));
          if (state.currentOrganization?.id === action.payload.id) {
            state.currentOrganization = action.payload;
          }
        }
        state.status = 'success';
        state.error = '';
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to update organization';
      });

    // Get Organization Stats
    builder
      .addCase(getOrganizationStats.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getOrganizationStats.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentOrganizationStats = action.payload;
        }
        state.status = 'success';
        state.error = '';
      })
      .addCase(getOrganizationStats.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to fetch organization stats';
      });

    // Add User to Organization
    builder
      .addCase(addUserToOrganization.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addUserToOrganization.fulfilled, (state) => {
        state.status = 'success';
        state.error = '';
      })
      .addCase(addUserToOrganization.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to add user to organization';
      });

    // switchOrganization removed - users can only belong to one organization
  }
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setSelectedOrganization, clearOrganizationsState } = organizationsSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectOrganizations = (state: any) => state.organizations.organizations;
export const selectCurrentOrganization = (state: any) => state.organizations.currentOrganization;
export const selectCurrentOrganizationStats = (state: any) => state.organizations.currentOrganizationStats;
export const selectSelectedOrganizationId = (state: any) => state.organizations.selectedOrganizationId;
export const selectOrganizationsStatus = (state: any) => state.organizations.status;
export const selectOrganizationsError = (state: any) => state.organizations.error;

export default organizationsSlice.reducer;
